import { ref, computed, watch, onUnmounted, toValue, type MaybeRefOrGetter } from 'vue';

export function useCountdown(epochSeconds: MaybeRefOrGetter<number>) {
  const now = ref(Date.now());
  let timer: ReturnType<typeof setInterval> | null = null;

  const start = () => {
    stop();
    timer = setInterval(() => {
      now.value = Date.now();
    }, 1000);
  };

  const stop = () => {
    if (timer) {
      clearInterval(timer);
      timer = null;
    }
  };

  const remainingMs = computed(() => {
    const target = toValue(epochSeconds) * 1000;
    return Math.max(0, target - now.value);
  });

  const isExpired = computed(() => remainingMs.value <= 0);
  const isUrgent = computed(() => remainingMs.value > 0 && remainingMs.value < 5 * 60 * 1000);

  const pad = (n: number) => String(n).padStart(2, '0');

  const label = computed(() => {
    const ms = remainingMs.value;
    if (ms <= 0) return 'Closed';

    const totalSeconds = Math.floor(ms / 1000);
    const days = Math.floor(totalSeconds / 86400);
    const hours = Math.floor((totalSeconds % 86400) / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;

    if (days >= 1) return `${days}d ${hours}h`;
    return `${pad(hours)}:${pad(minutes)}:${pad(seconds)}`;
  });

  watch(
    () => toValue(epochSeconds),
    () => {
      now.value = Date.now();
    },
  );

  start();
  onUnmounted(stop);

  return { label, remainingMs, isUrgent, isExpired };
}
